import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { exportNote, type ExportFormat } from "@/lib/export";
import { canUseExportFormat } from "@/lib/pro";
import { useIsPro } from "@/store/subscription";
import { useUIStore } from "@/store/ui";
import { ProTag } from "@/components/ui/ProTag";
import type { Note } from "@caliu/types";
import { Fragment, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getNote } from "@/lib/db";
import { updateFrontmatterReminders } from "@caliu/utils";
import { toast } from "sonner";
import { useIsMobile } from "../hooks/useIsMobile";
import { useCreateNote, useNote, useSetNoteReadOnly } from "../hooks/useNotes";
import { useSignInGate } from "../hooks/useSignInGate";
import {
  useAddTagToNote,
  useRemoveTagFromNote,
  useTags,
} from "../hooks/useTags";
import { getNoteTagIds } from "../lib/db";
import { Sentry } from "../lib/sentry";
import { tagColorClassName, tagColorStyle } from "../lib/tagColors";
import { tagFullPath } from "../lib/tagHierarchy";
import { TagIcon } from "../lib/tagIcons";
import { isSharedWithMe, isViewOnly, shareStatus, type ShareStatus } from "../lib/sharing";
import { useSharingFeature } from "../hooks/useSharingFeature";
import { useLegacySyncMode } from "../hooks/useLegacySyncMode";
import { useAuthStore } from "../store/auth";
import { LeaveNoteDialog } from "./sharing/LeaveNoteDialog";
import { Icon } from "@/components/ui/Icon";

const EXPORT_FORMATS: {
  format: ExportFormat;
  labelKey: string;
  /** Render a separator before this item — splits plain-text from rich formats. */
  separatorBefore?: boolean;
}[] = [
  { format: "markdown", labelKey: "noteActions.exportMarkdown" },
  { format: "txt", labelKey: "noteActions.exportPlainText" },
  { format: "textpack", labelKey: "noteActions.exportTextpack" },
  { format: "html", labelKey: "noteActions.exportHtml", separatorBefore: true },
  { format: "rtf", labelKey: "noteActions.exportRtf" },
  { format: "epub", labelKey: "noteActions.exportEpub" },
];

interface NoteActionsMenuProps {
  note: Pick<Note, "id" | "isPinned">;
  onPin?: () => void;
  onArchive?: () => void;
  onDelete: () => void;
  /** After leaving a note shared with this account (navigate away from it). */
  onLeft?: () => void;
  onSelect?: () => void;
  preFooterContent?: ReactNode;
  /**
   * Hide the "Make Read-Only" item. The editor header pill sets this because
   * on web the toggle lives permanently in the toolbar instead (only iPhone
   * puts it in the ⋯ menu). The note-list rows keep it.
   */
  hideReadOnlyToggle?: boolean;
}

/**
 * Shared dropdown menu items for note actions, in the native order
 * (NoteActionsMenu.swift): Pin (list rows), Share, Export | the caller's view
 * items (Zen, Find, Source Mode), Read-Only, Tags, Duplicate, Select |
 * Archive, Delete.
 * Must be used inside a DropdownMenuContent.
 */
export function NoteActionsMenu({
  note,
  onPin,
  onArchive,
  onDelete,
  onLeft,
  onSelect,
  preFooterContent,
  hideReadOnlyToggle,
}: NoteActionsMenuProps) {
  const { t } = useTranslation();
  const setNoteReadOnly = useSetNoteReadOnly();
  const isPro = useIsPro();
  const requireAccount = useSignInGate();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const { note: fullNote } = useNote(note.id);
  const meId = useAuthStore((s) => s.user?.id);
  const openShareSheet = useUIStore((s) => s.openShareSheet);
  const [leaveOpen, setLeaveOpen] = useState(false);
  // A note shared with this account: Leave replaces the trash (PLAN.md D5),
  // and a viewer cannot change its text (tags are hashtags in it).
  const isMember = isSharedWithMe(fullNote, meId);
  // Under Share: who already has the note (nothing while private, or before the beta).
  const sharingOn = useSharingFeature();
  const shareStatusText = sharingOn ? shareStatusLine(shareStatus(fullNote, meId), t) : null;
  const viewOnly = isViewOnly(fullNote, meId, useLegacySyncMode());
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const tags = useTags();
  const addTagToNote = useAddTagToNote();
  const removeTagFromNote = useRemoveTagFromNote();

  // Encrypted note content is ciphertext — never offer content rewrites.
  const canDuplicate = fullNote ? !fullNote.isEncrypted : false;
  const canAddTags = canDuplicate && !viewOnly;
  const noteTagIds = new Set(getNoteTagIds(note.id));

  const createNote = useCreateNote();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Same title and content (Bear does the same; the server derives the title
  // from content, so a "(copy)" suffix would not survive the first sync).
  // Not pinned, not read-only: a fresh note that happens to start full.
  const handleDuplicate = () => {
    // Land the pending autosave first (same guard useSetNoteReadOnly uses),
    // then read the row: `fullNote` may predate the last keystrokes.
    window.dispatchEvent(new Event("caliu-flush-autosave"));
    const source = getNote(note.id);
    if (!source) return;
    // Reminders ride the frontmatter with their ids; copied verbatim, the
    // worker would re-point the original's alarm at the copy.
    const content = updateFrontmatterReminders(source.content, []);
    const tags = getNoteTagIds(note.id);
    // Let the dropdown close before the route changes (see NoteHeaderPill).
    setTimeout(() => {
      createNote
        .mutateAsync({ title: source.title, content, tags }, "note_actions_menu")
        .then((copy) => {
          // Free note limit reached: the paywall is already open.
          if (!copy) return;
          const qs = searchParams.toString();
          navigate(`/app/note/${copy.id}${qs ? `?${qs}` : ""}`);
        })
        .catch((err) => {
          Sentry.captureException(err);
          toast.error(t("noteActions.duplicateFailed"));
        });
    }, 0);
  };

  // Pinned tags first, then alphabetical by full handle.
  const tagOptions = useMemo(() => {
    const byId = new Map(tags.map((item) => [item.id, item]));
    return tags
      .map((item) => ({ tag: item, handle: tagFullPath(item, byId) }))
      .sort((a, b) => {
        if (Boolean(a.tag.isPinned) !== Boolean(b.tag.isPinned)) {
          return a.tag.isPinned ? -1 : 1;
        }
        return a.handle.localeCompare(b.handle);
      });
  }, [tags]);

  const handleToggleTag = async (handle: string, isAttached: boolean) => {
    try {
      if (isAttached) {
        await removeTagFromNote.mutate({ noteId: note.id, handle });
      } else {
        await addTagToNote.mutate({ noteId: note.id, handle });
      }
    } catch (err) {
      Sentry.captureException(err);
      toast.error(
        t(isAttached ? "noteActions.removeTagFailed" : "noteActions.addTagFailed"),
      );
    }
  };

  const handleShare = () => {
    // Sharing lives on the server: without an account explain and open sign-in.
    if (requireAccount("share")) return;
    // Let the dropdown close before the dialog opens (focus returns first).
    setTimeout(() => openShareSheet(note.id, "note_actions_menu"), 0);
  };

  const handleExport = async (format: ExportFormat) => {
    if (!canUseExportFormat(isPro, format)) {
      openPaywall("export");
      return;
    }
    try {
      await exportNote(note.id, format);
    } catch {
      toast.error(t("noteActions.exportFailed"));
    }
  };

  return (
    <>
      {onPin && (
        <DropdownMenuItem onSelect={onPin}>
          {note.isPinned ? <Icon name="unpin" size={15} /> : <Icon name="pin" size={15} />}
          {note.isPinned ? t("noteActions.unpin") : t("noteActions.pinToTop")}
        </DropdownMenuItem>
      )}

      {!isMember && (
        <DropdownMenuItem onSelect={handleShare} className={shareStatusText ? "rounded-2xl" : undefined}>
          <Icon name="share" size={15} />
          {shareStatusText ? (
            <span className="flex min-w-0 flex-col">
              <span>{t("noteActions.share")}</span>
              <span className="truncate text-[11px] text-muted max-md:text-[13px]">{shareStatusText}</span>
            </span>
          ) : (
            t("noteActions.share")
          )}
        </DropdownMenuItem>
      )}

      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Icon name="file-down" size={15} />
          {t("noteActions.exportAs")}
          <Icon name="chevron-right" size={13} className="ml-auto opacity-50" />
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {EXPORT_FORMATS.map((item) => (
            <Fragment key={item.format}>
              {item.separatorBefore && <DropdownMenuSeparator />}
              <DropdownMenuItem onSelect={() => void handleExport(item.format)}>
                {t(item.labelKey)}
                {!canUseExportFormat(isPro, item.format) && (
                  <ProTag className="ml-auto" />
                )}
              </DropdownMenuItem>
            </Fragment>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
      {preFooterContent}
      {!hideReadOnlyToggle && fullNote && (
        <DropdownMenuItem
          onSelect={() =>
            setNoteReadOnly.mutate({ id: note.id, isReadOnly: !fullNote.isReadOnly })
          }
        >
          {fullNote.isReadOnly ? <Icon name="edit" size={15} /> : <Icon name="read-only" size={15} />}
          {fullNote.isReadOnly
            ? t("noteActions.makeEditable")
            : t("noteActions.makeReadOnly")}
        </DropdownMenuItem>
      )}

      {canAddTags &&
        (isMobile ? (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setAddTagDialogOpen(true);
              }}
            >
              <Icon name="tag" size={15} />
              {t("noteActions.tags")}
            </DropdownMenuItem>
            <Dialog open={addTagDialogOpen} onOpenChange={setAddTagDialogOpen}>
              <DialogContent className="max-w-xs">
                <DialogHeader>
                  <DialogTitle>
                    <Icon name="tag" size={14} />
                    {t("noteActions.tags")}
                  </DialogTitle>
                </DialogHeader>
                <div className="p-2 max-h-72 overflow-y-auto flex flex-col gap-0.5">
                  {tagOptions.length === 0 ? (
                    <p className="text-xs text-muted text-center py-3 m-0">
                      {t("noteActions.noTagsYet")}
                    </p>
                  ) : (
                    tagOptions.map(({ tag: tagItem, handle }) => {
                      const hasTag = noteTagIds.has(tagItem.id);
                      return (
                        <Button
                          key={tagItem.id}
                          variant="ghost"
                          size="sm"
                          className="justify-start w-full"
                          onClick={() => void handleToggleTag(handle, hasTag)}
                        >
                          <span
                            className={`flex-shrink-0 flex items-center ${
                              tagItem.color
                                ? (tagColorClassName(tagItem.color) ?? "")
                                : "text-muted"
                            }`}
                            style={tagColorStyle(tagItem.color)}
                          >
                            {tagItem.icon ? (
                              <TagIcon name={tagItem.icon} size={15} />
                            ) : (
                              <Icon name="tag" size={15} />
                            )}
                          </span>
                          <span className="truncate">#{handle}</span>
                          {hasTag && (
                            <Icon name="check" size={13} className="ml-auto text-accent" />
                          )}
                        </Button>
                      );
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon name="tag" size={15} />
              {t("noteActions.tags")}
              <Icon name="chevron-right" size={13} className="ml-auto opacity-50" />
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[190px] max-h-80 overflow-y-auto">
              {tagOptions.length === 0 ? (
                <p className="text-xs text-muted text-center px-3 py-2 m-0">
                  {t("noteActions.noTagsYet")}
                </p>
              ) : (
                tagOptions.map(({ tag: tagItem, handle }) => {
                  const hasTag = noteTagIds.has(tagItem.id);
                  return (
                    <DropdownMenuItem
                      key={tagItem.id}
                      onSelect={(e) => {
                        e.preventDefault();
                        void handleToggleTag(handle, hasTag);
                      }}
                    >
                      <span
                        className={`flex-shrink-0 flex items-center ${
                          tagItem.color
                            ? (tagColorClassName(tagItem.color) ?? "")
                            : "text-muted"
                        }`}
                        style={tagColorStyle(tagItem.color)}
                      >
                        {tagItem.icon ? (
                          <TagIcon name={tagItem.icon} size={15} />
                        ) : (
                          <Icon name="tag" size={15} />
                        )}
                      </span>
                      <span className="truncate">#{handle}</span>
                      {hasTag && (
                        <Icon name="check" size={13} className="ml-auto text-accent" />
                      )}
                    </DropdownMenuItem>
                  );
                })
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}

      {canDuplicate && (
        <DropdownMenuItem onSelect={handleDuplicate}>
          <Icon name="duplicate" size={15} />
          {t("noteActions.duplicate")}
        </DropdownMenuItem>
      )}
      {onSelect && (
        <DropdownMenuItem onSelect={onSelect}>
          <Icon name="checkbox" size={15} />
          {t("noteActions.select")}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      {onArchive && (
        <DropdownMenuItem onSelect={onArchive}>
          <Icon name="archive" size={15} />
          {t("noteActions.archive")}
        </DropdownMenuItem>
      )}
      {isMember ? (
        <>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setLeaveOpen(true);
            }}
            className="text-danger data-[highlighted]:text-danger"
          >
            <Icon name="sign-out" size={15} />
            {t("sharing.leaveNote")}
          </DropdownMenuItem>
          <LeaveNoteDialog
            noteId={note.id}
            open={leaveOpen}
            onOpenChange={setLeaveOpen}
            source="note_actions_menu"
            onLeft={onLeft}
          />
        </>
      ) : (
        <DropdownMenuItem
          onSelect={onDelete}
          className="text-danger data-[highlighted]:text-danger"
        >
          <Icon name="trash" size={15} />
          {t("noteActions.delete")}
        </DropdownMenuItem>
      )}
    </>
  );
}

/** The Share item's second line (`shareStatus`), or null for a private note. */
function shareStatusLine(status: ShareStatus, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  switch (status.kind) {
    case "none":
      return null;
    case "people":
      return t("sharing.statusPeople", { count: status.count });
    case "peopleAndLink":
      return `${t("sharing.statusPeople", { count: status.count })} · ${t("sharing.publicLink")}`;
    case "link":
      return status.access === "edit" ? `${t("sharing.publicLink")} · ${t("sharing.roleEditor")}` : t("sharing.publicLink");
    case "sharedBy": {
      const role = t(status.role === "editor" ? "sharing.roleEditor" : "sharing.roleViewer");
      return status.name ? `${t("sharing.statusFrom", { name: status.name })} · ${role}` : role;
    }
  }
}
